"use client";

import { Text, SectionCard, CardTitle, CardDescription, Badge, Section } from "@/src/components";

export default function DocsBlocksSidebar() {
  return (
    <>
      {/* Header */}
      <Section>
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <Text as="h1" className="text-h2 font-bold">
              Sidebar
            </Text>
            <Badge variant="secondary">Block</Badge>
          </div>
          <Text as="p" className="text-lg text-muted-foreground max-w-2xl">
            A collapsible navigation sidebar with support for sections, search,
            and active state detection. This documentation site uses this component.
          </Text>
        </div>
      </Section>

      {/* Features */}
      <Section>
        <SectionCard
        header={<CardTitle>Features</CardTitle>}
        contentClassName="p-6"
      >
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <div className="flex items-center gap-2">
            <Badge variant="secondary">-</Badge>
            <Text as="p" className="text-sm">Collapsible sections</Text>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="secondary">-</Badge>
            <Text as="p" className="text-sm">Search filtering</Text>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="secondary">-</Badge>
            <Text as="p" className="text-sm">Active state detection</Text>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="secondary">-</Badge>
            <Text as="p" className="text-sm">Collapse/expand toggle</Text>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="secondary">-</Badge>
            <Text as="p" className="text-sm">Icon support</Text>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="secondary">-</Badge>
            <Text as="p" className="text-sm">Badge indicators</Text>
          </div>
        </div>
      </SectionCard>
      </Section>

      {/* Usage */}
      <Section>
        <SectionCard
        header={
          <>
            <CardTitle>Basic Usage</CardTitle>
            <CardDescription>Setting up a sidebar with navigation</CardDescription>
          </>
        }
        contentClassName="p-6"
      >
        <div className="bg-muted/50 rounded-lg p-4 font-mono text-sm overflow-x-auto">
          <pre>{`import { Sidebar, SidebarLayout } from "@/registry/new-york/blocks/sidebar/sidebar";

const navigation = [
  {
    title: "Section",
    subsections: [
      {
        title: "Getting Started",
        items: [
          { label: "Introduction", href: "/docs", icon: <BookIcon /> },
          { label: "Installation", href: "/docs/install", icon: <ZapIcon /> },
        ],
      },
    ],
  },
];

<SidebarLayout
  sidebar={
    <Sidebar
      position="left"
      width={280}
      searchable
      logo={<Logo />}
      header={<div>My App</div>}
      navigation={navigation}
    />
  }
>
  <main>{children}</main>
</SidebarLayout>`}</pre>
        </div>
      </SectionCard>
      </Section>

      {/* Navigation Structure */}
      <Section>
        <SectionCard
        header={
          <>
            <CardTitle>Navigation Structure</CardTitle>
            <CardDescription>How to define navigation items</CardDescription>
          </>
        }
        contentClassName="p-6"
      >
        <div className="bg-muted/50 rounded-lg p-4 font-mono text-sm overflow-x-auto">
          <pre>{`type SidebarNavigationGroup = {
  title: string;
  subsections: {
    title: string;
    icon?: ReactNode;
    items: {
      label: string;
      href: string;
      icon?: ReactNode;
      badge?: ReactNode;
      disabled?: boolean;
    }[];
  }[];
};`}</pre>
        </div>
      </SectionCard>
      </Section>

      {/* Import */}
      <Section>
        <SectionCard
          header={<CardTitle>Import</CardTitle>}
          contentClassName="p-6"
        >
          <div className="bg-muted/50 rounded-lg p-4 font-mono text-sm space-y-2">
            <code className="block">{`import { Sidebar, SidebarLayout } from "@/registry/new-york/blocks/sidebar/sidebar";`}</code>
            <code className="block">{`import type { SidebarNavigationGroup } from "@/registry/new-york/blocks/sidebar/sidebar";`}</code>
          </div>
        </SectionCard>
      </Section>

      {/* Props */}
      <Section>
        <SectionCard
        header={<CardTitle>Props</CardTitle>}
        contentClassName="p-6"
      >
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b">
              <th className="text-left py-2 font-semibold">Prop</th>
              <th className="text-left py-2 font-semibold">Type</th>
              <th className="text-left py-2 font-semibold">Default</th>
              <th className="text-left py-2 font-semibold">Description</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-b">
              <td className="py-2 font-mono text-xs">position</td>
              <td className="py-2 font-mono text-xs text-muted-foreground">&quot;left&quot; | &quot;right&quot;</td>
              <td className="py-2 font-mono text-xs">&quot;left&quot;</td>
              <td className="py-2 text-muted-foreground">Sidebar position</td>
            </tr>
            <tr className="border-b">
              <td className="py-2 font-mono text-xs">width</td>
              <td className="py-2 font-mono text-xs text-muted-foreground">number | string</td>
              <td className="py-2 font-mono text-xs">280</td>
              <td className="py-2 text-muted-foreground">Sidebar width</td>
            </tr>
            <tr className="border-b">
              <td className="py-2 font-mono text-xs">collapsed</td>
              <td className="py-2 font-mono text-xs text-muted-foreground">boolean</td>
              <td className="py-2 font-mono text-xs">false</td>
              <td className="py-2 text-muted-foreground">Controlled collapsed state</td>
            </tr>
            <tr className="border-b">
              <td className="py-2 font-mono text-xs">onCollapsedChange</td>
              <td className="py-2 font-mono text-xs text-muted-foreground">(collapsed: boolean) =&gt; void</td>
              <td className="py-2 font-mono text-xs">-</td>
              <td className="py-2 text-muted-foreground">Collapse state change handler</td>
            </tr>
            <tr className="border-b">
              <td className="py-2 font-mono text-xs">searchable</td>
              <td className="py-2 font-mono text-xs text-muted-foreground">boolean</td>
              <td className="py-2 font-mono text-xs">false</td>
              <td className="py-2 text-muted-foreground">Enable search</td>
            </tr>
            <tr className="border-b">
              <td className="py-2 font-mono text-xs">navigation</td>
              <td className="py-2 font-mono text-xs text-muted-foreground">SidebarNavigationGroup[]</td>
              <td className="py-2 font-mono text-xs">-</td>
              <td className="py-2 text-muted-foreground">Navigation configuration</td>
            </tr>
            <tr className="border-b">
              <td className="py-2 font-mono text-xs">logo</td>
              <td className="py-2 font-mono text-xs text-muted-foreground">ReactNode</td>
              <td className="py-2 font-mono text-xs">-</td>
              <td className="py-2 text-muted-foreground">Logo element</td>
            </tr>
            <tr className="border-b">
              <td className="py-2 font-mono text-xs">header</td>
              <td className="py-2 font-mono text-xs text-muted-foreground">ReactNode</td>
              <td className="py-2 font-mono text-xs">-</td>
              <td className="py-2 text-muted-foreground">Header content next to logo</td>
            </tr>
            <tr className="border-b">
              <td className="py-2 font-mono text-xs">footer</td>
              <td className="py-2 font-mono text-xs text-muted-foreground">ReactNode</td>
              <td className="py-2 font-mono text-xs">-</td>
              <td className="py-2 text-muted-foreground">Footer content</td>
            </tr>
          </tbody>
        </table>
      </SectionCard>
      </Section>
    </>
  );
}
